/**
 * Untrusted URL trust checks, shared by every client that renders a URL the
 * platform did not mint.
 *
 * Stream Chat's default `messaging` channel type lets any channel member
 * mutate `channel.image` and set attachment url fields (`image_url`,
 * `asset_url`, `thumb_url`, ...) verbatim, so those strings are
 * attacker-controlled. The mobile client used to forward them to
 * `<Image source={{ uri }}>` unvalidated (findings MO-CHAT-T1-SEC-006 /
 * -007); the same values now also reach a school admin's browser through the
 * chat-moderation surface, which is why the check lives here rather than in
 * one app.
 *
 * Two tiers, because the two hazards are different:
 *
 *   - `isSafeMediaUrl` — https AND an allowlisted host. For anything a client
 *     fetches with NO user action (an `<img src>`, an avatar). An arbitrary
 *     host there is a silent beacon: it confirms a viewer opened the content
 *     and leaks their IP, before any click happens.
 *   - `isHttpsUrl` — scheme only. For a URL a human must deliberately click
 *     (a link preview to a news site a parent pasted). The host is
 *     legitimately arbitrary there, so a host allowlist would break the
 *     feature; the scheme check is what kills `javascript:`, `data:` and
 *     `file:`.
 */

const ALLOWED_PROTOCOLS = new Set(['https:'])

/**
 * Hosts considered safe to auto-load media from.
 *
 * Suffix-matched (so `res.cloudinary.com` matches `cloudinary.com` and
 * `foo.s3.amazonaws.com` matches `amazonaws.com`). Keep this list narrow —
 * every host added widens the trust boundary for every surface that renders
 * untrusted media, on every client.
 */
export const ALLOWED_MEDIA_HOST_SUFFIXES: readonly string[] = [
  'cloudinary.com',
  'amazonaws.com',
  'cloudfront.net',
  'mux.com',
  'muxcdn.com',
  'schooljourney.ai',
  'schooljourney.com',
  'schooljourney.app',
  'getstream.io',
  'stream-io-cdn.com'
]

/** Parses `url`, or returns null for anything malformed or non-string. */
function parseUrl(url: string | null | undefined): URL | null {
  if (!url || typeof url !== 'string') return null
  try {
    return new URL(url)
  } catch {
    return null
  }
}

/**
 * True iff `url` parses cleanly and uses an allowed protocol (https only).
 *
 * The weaker of the two checks: it says nothing about WHERE the url points.
 * Use it only where a human click stands between the value and any fetch.
 */
export function isHttpsUrl(url: string | null | undefined): boolean {
  const parsed = parseUrl(url)
  return parsed != null && ALLOWED_PROTOCOLS.has(parsed.protocol)
}

/**
 * True iff `url` parses cleanly, uses an allowed protocol, and lives on an
 * allowlisted host. Returns false for anything malformed.
 */
export function isSafeMediaUrl(url: string | null | undefined): boolean {
  const parsed = parseUrl(url)
  if (parsed == null || !ALLOWED_PROTOCOLS.has(parsed.protocol)) return false

  const host = parsed.hostname.toLowerCase()
  if (!host) return false

  return ALLOWED_MEDIA_HOST_SUFFIXES.some(suffix => host === suffix || host.endsWith(`.${suffix}`))
}

/**
 * Returns `url` if it passes the media allowlist, otherwise `fallback`.
 * Use at every untrusted-media render site.
 */
export function safeMediaUrlOr<T>(url: string | null | undefined, fallback: T): string | T {
  return isSafeMediaUrl(url) ? (url as string) : fallback
}

/**
 * Returns `url` if it is an https url, otherwise `fallback`. For clickable
 * links only — see the module header for why this tier exists.
 */
export function httpsUrlOr<T>(url: string | null | undefined, fallback: T): string | T {
  return isHttpsUrl(url) ? (url as string) : fallback
}

/**
 * The `webcal:` / `webcals:` scheme, case-insensitively, at the very front of a
 * string. Nothing after the `//` is touched.
 */
const WEBCAL_SCHEME_PATTERN = /^webcals?:\/\//i

/**
 * Rewrites a `webcal://` (or `webcals://`) calendar address to its `https://`
 * twin, leaving every other string exactly as it came in.
 *
 * `webcal:` is not a protocol and has no RFC. It is an Apple/Outlook convention
 * meaning "hand this .ics to the calendar app and subscribe" rather than
 * "download it in the browser": the client strips the scheme and refetches the
 * identical body over HTTP(S). So a `webcal://` address and its `https://` twin
 * name the same document, and our adapter — which polls on its own schedule —
 * already provides the subscription half. Rewriting the scheme is therefore a
 * pure input-edge convenience, not a loosening of what we will fetch: the
 * result is an ordinary https url and every downstream guard (scheme check,
 * SSRF filter, no-redirect fetch, size cap) applies to it unchanged.
 *
 * CALENDAR FEED URLS ONLY. A web-feed item's `link` or `imageUrl` is a url a
 * publisher wrote, not one an author pasted, and silently rewriting a scheme
 * there would be inventing an address nobody chose.
 *
 * IDEMPOTENT AND CANONICAL: normalizing `webcal://x/y.ics` and `https://x/y.ics`
 * yields the identical string, which is what lets a re-paste of the same
 * calendar be recognized as the same feed rather than a new one.
 *
 * `webcals://` collapses to `https://` as well — it is the same convention with
 * the TLS half made explicit, and https is the only scheme we fetch anyway.
 */
export function normalizeCalendarFeedUrl(url: string): string {
  return url.replace(WEBCAL_SCHEME_PATTERN, 'https://')
}
